/**
 * Razorpay Payment Integration for Shopify — Rosetrienity
 *
 * Flow:
 *   1. Customer clicks "Checkout"
 *   2. Razorpay modal opens
 *   3. Customer pays via Razorpay (Card / UPI / Net Banking / Wallet)
 *   4. Cart is stamped with payment ID + attributes
 *   5. Customer is redirected to /checkout to enter shipping address
 *   6. Customer selects Cash on Delivery (payment already collected)
 *   7. Order is created in Shopify
 *
 * IMPORTANT: Switch razorpay_key_id in Theme Settings from rzp_test_... to rzp_live_...
 *            before going live. Test credentials are only shown in test mode.
 */

(function () {
  'use strict';

  const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

  // ── Test mode detection ───────────────────────────────────────
  function isTestMode(keyId) {
    return typeof keyId === 'string' && keyId.startsWith('rzp_test_');
  }

  // ── Singleton script loader ───────────────────────────────────
  let scriptLoaded = false, scriptLoading = false;
  const scriptCbs = [];

  function loadRazorpayScript(cb) {
    if (scriptLoaded) { cb(null); return; }
    scriptCbs.push(cb);
    if (scriptLoading) return;
    scriptLoading = true;
    const s = document.createElement('script');
    s.src = RAZORPAY_SCRIPT_URL;
    s.async = true;
    s.onload  = () => { scriptLoaded = true; scriptLoading = false; scriptCbs.forEach(fn => fn(null)); scriptCbs.length = 0; };
    s.onerror = () => { scriptLoading = false; const e = new Error('Razorpay SDK load failed'); scriptCbs.forEach(fn => fn(e)); scriptCbs.length = 0; };
    document.head.appendChild(s);
  }

  // ── Cart helpers ──────────────────────────────────────────────
  async function fetchCart() {
    const r = await fetch('/cart.js');
    if (!r.ok) throw new Error('Cart fetch failed');
    return r.json();
  }

  async function stampCartWithPayment(paymentId, amountInPaise) {
    const ts  = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
    const amt = (amountInPaise / 100).toFixed(2);
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        note: `✅ Paid via Razorpay | ID: ${paymentId} | ₹${amt} | ${ts}`,
        attributes: {
          _rzp_payment_id: paymentId,
          _rzp_amount:     '₹' + amt,
          _rzp_paid:       'true',
          _rzp_time:       ts,
        },
      }),
    });
  }

  // ── UI helpers ────────────────────────────────────────────────
  function setCheckoutLoading(btn, loading) {
    if (!btn) return;
    const textWrap = btn.querySelector('.rzp-btn__text, .button-text');
    const spinner  = btn.querySelector('.rzp-btn__spinner');
    btn.disabled = loading;

    if (spinner) {
      spinner.style.display = loading ? 'flex' : 'none';
    }
    if (textWrap) {
      textWrap.style.opacity = loading ? '0' : '1';
    }
  }

  function showError(msg) {
    document.querySelectorAll('.rzp-error-toast').forEach(el => el.remove());
    const t = document.createElement('div');
    t.className = 'rzp-error-toast';
    t.setAttribute('role', 'alert');
    t.innerHTML = `
      <span class="rzp-error-toast__icon">⚠</span>
      <span class="rzp-error-toast__message">${msg}</span>
      <button class="rzp-error-toast__close" onclick="this.parentElement.remove()">✕</button>
    `;
    document.body.appendChild(t);
    setTimeout(() => t.remove(), 9000);
  }

  function showTestBanner(amountInPaise) {
    document.querySelectorAll('.rzp-test-banner').forEach(el => el.remove());
    const amt = (amountInPaise / 100).toFixed(2);
    const b = document.createElement('div');
    b.className = 'rzp-test-banner';
    b.innerHTML = `
      <div class="rzp-test-banner__inner">
        <div class="rzp-test-banner__header">
          <span class="rzp-test-banner__badge">🧪 TEST MODE — Developer Only</span>
          <button class="rzp-test-banner__close" onclick="this.closest('.rzp-test-banner').remove()">✕</button>
        </div>
        <p class="rzp-test-banner__title">Use test credentials to pay <strong>₹${amt}</strong></p>
        <div class="rzp-test-banner__grid">
          <div class="rzp-test-banner__method">
            <div class="rzp-test-banner__method-title">💳 Test Card</div>
            <div class="rzp-test-banner__row">
              <span class="rzp-test-banner__label">Number</span>
              <button class="rzp-test-banner__copy" onclick="navigator.clipboard.writeText('4111111111111111');this.textContent='✅';setTimeout(()=>this.textContent='Copy',1500)">Copy</button>
              <code class="rzp-test-banner__value">4111 1111 1111 1111</code>
            </div>
            <div class="rzp-test-banner__row">
              <span class="rzp-test-banner__label">Expiry / CVV / OTP</span>
              <code class="rzp-test-banner__value">12/25 &nbsp;|&nbsp; 123 &nbsp;|&nbsp; 1234</code>
            </div>
          </div>
          <div class="rzp-test-banner__method">
            <div class="rzp-test-banner__method-title">📱 Test UPI</div>
            <div class="rzp-test-banner__row">
              <span class="rzp-test-banner__label">Test UPI ID</span>
              <button class="rzp-test-banner__copy" onclick="navigator.clipboard.writeText('success@razorpay');this.textContent='✅';setTimeout(()=>this.textContent='Copy',1500)">Copy</button>
              <code class="rzp-test-banner__value">success@razorpay</code>
            </div>
            <div class="rzp-test-banner__tip">⚠️ Type the UPI ID manually — don't scan QR code</div>
          </div>
        </div>
        <div class="rzp-test-banner__steps">
          <strong>After payment:</strong> Enter your shipping address → select <em>Cash on Delivery</em> → Order confirmed ✅
        </div>
        <div class="rzp-test-banner__warning">⚠️ This banner is only visible in test mode. Switch to a Live Key (rzp_live_...) before going live.</div>
      </div>
    `;
    document.body.appendChild(b);
  }

  function showSuccessOverlay(paymentId, amountInPaise, testMode) {
    document.querySelectorAll('.rzp-test-banner').forEach(el => el.remove());
    const amt = (amountInPaise / 100).toFixed(2);
    const o = document.createElement('div');
    o.className = 'rzp-success-overlay';
    o.innerHTML = `
      <div class="rzp-success-overlay__card">
        <div class="rzp-success-overlay__icon">
          <svg viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="26" cy="26" r="26" fill="#22c55e"/>
            <path d="M14 26l9 9 15-17" stroke="#fff" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h2 class="rzp-success-overlay__title">Payment Successful! 🎉</h2>
        <p class="rzp-success-overlay__subtitle">₹${amt} paid via Razorpay</p>
        <p class="rzp-success-overlay__id">Payment ID: <strong>${paymentId}</strong></p>
        <div class="rzp-success-overlay__info">
          <p class="rzp-success-overlay__info-text">📦 Next: Enter your delivery address</p>
          <p class="rzp-success-overlay__info-text">💳 Select <strong>Cash on Delivery</strong> at checkout<br><span style="font-size:0.8em;opacity:0.7">(your payment is already collected via Razorpay)</span></p>
        </div>
        <div class="rzp-success-overlay__loader"><div class="rzp-success-overlay__loader-bar"></div></div>
        <p class="rzp-success-overlay__redirect">Redirecting to enter your shipping address…</p>
      </div>
    `;
    document.body.appendChild(o);
    document.body.style.overflow = 'hidden';
  }

  // ── Core: open Razorpay then continue checkout ────────────────
  async function handleCheckoutWithRazorpay(btn) {
    const config = window.RazorpayConfig || {};
    if (!config.keyId) {
      // No key set — fall through to native checkout
      return false;
    }

    const testMode = isTestMode(config.keyId);

    // Show loading on the button
    setCheckoutLoading(btn, true);

    try {
      const cart = await fetchCart();

      if (cart.item_count === 0) {
        setCheckoutLoading(btn, false);
        return false; // let native checkout handle empty cart
      }

      const amountInPaise = cart.total_price;

      if (amountInPaise < 100) {
        setCheckoutLoading(btn, false);
        return false;
      }

      if (testMode) showTestBanner(amountInPaise);

      await new Promise((res, rej) => loadRazorpayScript(err => err ? rej(err) : res()));

      await new Promise((resolve, reject) => {
        const options = {
          key:         config.keyId,
          amount:      amountInPaise,
          currency:    'INR',
          name:        config.merchantName || config.shopName || 'Store',
          description: config.description  || 'Order Payment',
          image:       config.logoUrl || '',
          theme: {
            color:       config.themeColor || '#072654',
            hide_topbar: false,
          },
          prefill: {
            name:    config.customerName  || '',
            email:   config.customerEmail || '',
            contact: config.customerPhone || '',
          },
          notes: {
            shopify_cart_token: cart.token,
            source: 'shopify_theme',
          },
          // Open Card tab first — not QR
          config: {
            display: {
              blocks: {
                pay_methods: {
                  name: 'Pay Now',
                  instruments: [
                    { method: 'card' },
                    { method: 'netbanking' },
                    { method: 'upi',    flows: ['collect'], apps: [] },
                    { method: 'wallet' },
                  ],
                },
              },
              sequence: ['block.pay_methods'],
              preferences: { show_default_blocks: false },
            },
          },
          modal: {
            ondismiss() {
              setCheckoutLoading(btn, false);
              document.querySelectorAll('.rzp-test-banner').forEach(el => el.remove());
              reject(new Error('dismissed'));
            },
            animation:     true,
            backdropclose: false,
            escape:        true,
            confirm_close: true,
          },
          handler: async function (response) {
            const paymentId = response.razorpay_payment_id;
            try {
              await stampCartWithPayment(paymentId, amountInPaise);
            } catch (e) {
              console.warn('[Razorpay] Stamp failed:', e);
            }
            showSuccessOverlay(paymentId, amountInPaise, testMode);
            setTimeout(() => { window.location.href = '/checkout'; }, 3000);
            resolve(paymentId);
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response) {
          setCheckoutLoading(btn, false);
          document.querySelectorAll('.rzp-test-banner').forEach(el => el.remove());
          showError('❌ Payment failed: ' + (response.error.description || 'Please try again.'));
          console.error('[Razorpay] Failed:', response.error);
          reject(new Error(response.error.description));
        });

        setCheckoutLoading(btn, false);
        rzp.open();
      });

      return true; // payment handled — don't submit the form

    } catch (err) {
      setCheckoutLoading(btn, false);
      document.querySelectorAll('.rzp-test-banner').forEach(el => el.remove());

      if (err.message === 'dismissed') {
        // User closed the modal — do nothing, let them try again
        return true;
      }

      // Any other error — fall through to native Shopify checkout
      console.warn('[Razorpay] Error, falling back to native checkout:', err);
      return false;
    }
  }

  // ── Intercept checkout button / form submission ───────────────
  function init() {
    // Intercept clicks on the checkout button that has data-razorpay-checkout
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-razorpay-checkout]');
      if (!btn) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      handleCheckoutWithRazorpay(btn).then(handled => {
        if (!handled) {
          // Razorpay not configured or error → submit the cart form natively
          const form = document.getElementById('cart-form')
                    || btn.closest('form')
                    || document.querySelector('form[action="/checkout"]');
          if (form) form.submit();
          else window.location.href = '/checkout';
        }
      });
    }, true); // capture phase to intercept before other handlers
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
